// 📂 frontend/src/components/EmailList.tsx
import React, { useState } from 'react';
import DOMPurify from 'dompurify';
import { useTheme } from '../hooks/useTheme';
import { useToast } from '../hooks/useToast';
import ConfirmModal from './ConfirmModal';
import { useConfirm } from '../hooks/useConfirm';
import emailApi from '../services/emailApi';

interface Email {
  id: number;
  to: string;
  subject: string;
  content: string;
  scheduledFor: Date;
  status: 'scheduled' | 'sent' | 'failed';
  isRecurring?: boolean;
  recurringType?: string;
  reminderDays?: number;
  priority?: string;
  emailType?: string;
  sentAt?: Date;
  nextRecurrence?: Date;
  reminderSentAt?: Date;
  errorMessage?: string;
}

interface EmailListProps {
  emails: Email[];
  onEmailView: (email: Email) => void;
  onEmailEdit: (email: Email) => void;
  onEmailDelete: (id: number) => Promise<void>;
  onBulkDelete?: (ids: number[]) => Promise<void>;
  onDeleteAll?: () => Promise<void>;
  onRefresh?: () => void;
  isLoading?: boolean;
}

const EmailList: React.FC<EmailListProps> = ({
  emails,
  onEmailView,
  onEmailEdit,
  onEmailDelete,
  onBulkDelete,
  onDeleteAll,
  onRefresh,
  isLoading = false
}) => {
  const { theme, currentTheme } = useTheme();
  const { toast } = useToast();
  const { confirmState, confirm, cancelConfirm } = useConfirm();
  const [expandedEmails, setExpandedEmails] = useState<Set<number>>(new Set());
  const [selectedEmails, setSelectedEmails] = useState<Set<number>>(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'scheduled' | 'sent' | 'failed' | 'upcoming'>('all');
  const [previewEmail, setPreviewEmail] = useState<Email | null>(null);
  const [duplicatingIds, setDuplicatingIds] = useState<Set<number>>(new Set());
  const [retryingIds, setRetryingIds] = useState<Set<number>>(new Set());
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkEditData, setBulkEditData] = useState({ scheduled_time: '', priority: '', status: '' });

  // Toggle expand pentru detalii
  const toggleExpand = (emailId: number) => {
    const newExpanded = new Set(expandedEmails);
    if (newExpanded.has(emailId)) {
      newExpanded.delete(emailId);
    } else {
      newExpanded.add(emailId);
    }
    setExpandedEmails(newExpanded);
  };

  // Toggle selecție email
  const toggleSelectEmail = (emailId: number) => {
    const newSelected = new Set(selectedEmails);
    if (newSelected.has(emailId)) {
      newSelected.delete(emailId);
    } else {
      newSelected.add(emailId);
    }
    setSelectedEmails(newSelected);
    
    // Reset select all if manually deselecting
    if (newSelected.size !== filteredEmails.length) {
      setSelectAll(false);
    }
  };

  // Toggle select all
  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedEmails(new Set());
    } else {
      setSelectedEmails(new Set(filteredEmails.map(e => e.id)));
    }
    setSelectAll(!selectAll);
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedEmails.size === 0) {
      toast.warning('Atenție', 'Selectează cel puțin un email');
      return;
    }

    const confirmed = await confirm({
      title: 'Confirmare ștergere',
      message: `Sigur vrei să ștergi ${selectedEmails.size} email${selectedEmails.size > 1 ? '-uri' : ''}?`,
      variant: 'danger',
      confirmText: 'Șterge',
      cancelText: 'Anulează'
    });
    if (!confirmed) return;

    setDeleting(true);
    try {
      if (onBulkDelete) {
        await onBulkDelete(Array.from(selectedEmails));
      } else {
        // Fallback to individual deletes
        for (const id of selectedEmails) {
          await onEmailDelete(id);
        }
      }
      
      const count = selectedEmails.size;
      setSelectedEmails(new Set());
      setSelectAll(false);
      toast.success('Succes', `${count} email${count > 1 ? '-uri' : ''} șterse cu succes`);
    } catch (error) {
      console.error('Error bulk deleting:', error);
      toast.error('Eroare', 'Nu am putut șterge email-urile');
    } finally {
      setDeleting(false);
    }
  };

  // Handle delete all
  const handleDeleteAll = async () => {
    const confirmMsg = statusFilter === 'all' 
      ? 'Sigur vrei să ștergi TOATE email-urile?' 
      : `Sigur vrei să ștergi toate email-urile ${statusFilter === 'scheduled' ? 'programate' : statusFilter === 'sent' ? 'trimise' : 'eșuate'}?`;

    const confirmed = await confirm({
      title: 'Confirmare ștergere totală',
      message: confirmMsg + '\n\nAceastă acțiune este IREVERSIBILĂ!',
      variant: 'danger',
      confirmText: 'Șterge tot',
      cancelText: 'Anulează'
    });
    if (!confirmed) return;

    setDeleting(true);
    try {
      if (onDeleteAll) {
        await onDeleteAll();
      } else {
        // Fallback to bulk delete
        const idsToDelete = filteredEmails.map(e => e.id);
        if (onBulkDelete) {
          await onBulkDelete(idsToDelete);
        }
      }
      
      setSelectedEmails(new Set());
      setSelectAll(false);
      toast.success('Succes', 'Toate email-urile au fost șterse');
    } catch (error) {
      console.error('Error deleting all:', error);
      toast.error('Eroare', 'Nu am putut șterge email-urile');
    } finally {
      setDeleting(false);
    }
  };

  // Handle individual delete
  const handleSingleDelete = async (emailId: number) => {
    const confirmed = await confirm({
      title: 'Confirmare ștergere',
      message: 'Sigur vrei să ștergi acest email?',
      variant: 'danger',
      confirmText: 'Șterge',
      cancelText: 'Anulează'
    });
    if (!confirmed) return;
    
    // Adaugă ID-ul în set-ul de ștergere pentru a dezactiva butonul
    setDeletingIds(prev => new Set([...prev, emailId]));
    
    try {
      await onEmailDelete(emailId);
      
      // Remove from selected if it was selected
      if (selectedEmails.has(emailId)) {
        const newSelected = new Set(selectedEmails);
        newSelected.delete(emailId);
        setSelectedEmails(newSelected);
      }
      
      toast.success('Succes', 'Email șters cu succes');
    } catch (error) {
      console.error('Error deleting email:', error);
      toast.error('Eroare', 'Nu am putut șterge email-ul');
    } finally {
      // Elimină ID-ul din set după ștergere
      setDeletingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(emailId);
        return newSet;
      });
    }
  };

  // Handle duplicate
  const handleDuplicate = async (emailId: number) => {
    setDuplicatingIds(prev => new Set([...prev, emailId]));
    try {
      await emailApi.duplicateEmail(emailId);
      toast.success('Succes', 'Email-ul a fost duplicat');
      if (onRefresh) onRefresh();
    } catch {
      toast.error('Eroare', 'Nu am putut duplica email-ul');
    } finally {
      setDuplicatingIds(prev => { const s = new Set(prev); s.delete(emailId); return s; });
    }
  };

  // Handle retry
  const handleRetry = async (emailId: number) => {
    setRetryingIds(prev => new Set([...prev, emailId]));
    try {
      await emailApi.retryEmail(emailId);
      toast.success('Succes', 'Email-ul a fost reprogramat');
      if (onRefresh) onRefresh();
    } catch {
      toast.error('Eroare', 'Nu am putut reprograma email-ul');
    } finally {
      setRetryingIds(prev => { const s = new Set(prev); s.delete(emailId); return s; });
    }
  };

  // Handle CSV export
  const handleExportCsv = () => {
    window.open(emailApi.getExportEmailsCsvUrl(), '_blank');
  };

  // Filtrare email-uri
  const filteredEmails = emails.filter(email => {
    const matchesSearch =
      email.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      email.to.toLowerCase().includes(searchTerm.toLowerCase());

    let matchesStatus = false;
    if (statusFilter === 'all') {
      matchesStatus = true;
    } else if (statusFilter === 'upcoming') {
      const now = new Date();
      const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      const scheduledDate = new Date(email.scheduledFor);
      matchesStatus = email.status === 'scheduled' && scheduledDate >= now && scheduledDate <= sevenDaysFromNow;
    } else {
      matchesStatus = email.status === statusFilter;
    }

    return matchesSearch && matchesStatus;
  });

  // Calculează următoarea recurență
  const calculateNextRecurrence = (email: Email): Date | null => {
    if (!email.isRecurring || !email.sentAt) return null;
    
    const sentDate = new Date(email.sentAt);
    const nextDate = new Date(sentDate);
    
    switch (email.recurringType) {
      case 'daily':
        nextDate.setDate(nextDate.getDate() + 1);
        break;
      case 'weekly':
        nextDate.setDate(nextDate.getDate() + 7);
        break;
      case 'monthly':
        nextDate.setMonth(nextDate.getMonth() + 1);
        break;
      case 'yearly':
        nextDate.setFullYear(nextDate.getFullYear() + 1);
        break;
      default:
        return null;
    }
    
    return nextDate;
  };

  // Obține badge pentru status
  const getStatusBadge = (email: Email) => {
    const badges = [];
    
    // Status principal
    badges.push({
      key: 'status',
      text: email.status === 'sent' ? 'Trimis' : email.status === 'scheduled' ? 'Programat' : 'Eșuat',
      color: email.status === 'sent' ? theme.colors.success : 
             email.status === 'scheduled' ? theme.colors.primary : theme.colors.danger,
      icon: email.status === 'sent' ? '✅' : email.status === 'scheduled' ? '⏰' : '❌'
    });
    
    // Recurring
    if (email.isRecurring) {
      badges.push({
        key: 'recurring',
        text: `Recurent (${email.recurringType})`,
        color: theme.colors.accent,
        icon: '🔄'
      });
    }
    
    // Priority
    if (email.priority && email.priority !== 'normal') {
      badges.push({
        key: 'priority',
        text: email.priority,
        color: email.priority === 'urgent' ? theme.colors.danger : 
               email.priority === 'high' ? theme.colors.warning : theme.colors.primary,
        icon: email.priority === 'urgent' ? '🔴' : email.priority === 'high' ? '🟡' : '🔵'
      });
    }
    
    // Reminder
    if (email.reminderDays && email.reminderDays > 0) {
      badges.push({
        key: 'reminder',
        text: `Reminder ${email.reminderDays}d`,
        color: theme.colors.warning,
        icon: '🔔'
      });
    }
    
    return badges;
  };

  // Formatare conținut pentru preview
  const getContentPreview = (content: string, maxLength: number = 150) => {
    // Strip HTML tags pentru preview
    const textContent = content.replace(/<[^>]*>/g, ' ').trim();
    
    if (textContent.length <= maxLength) return textContent;
    return textContent.substring(0, maxLength) + '...';
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div className="spinner" style={{ margin: '0 auto' }}></div>
        <p style={{ marginTop: '16px', color: theme.colors.textSecondary }}>
          Se încarcă email-urile...
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Header cu controale */}
      <div style={{
        backgroundColor: theme.colors.surface,
        padding: '20px',
        borderRadius: theme.borderRadius.large,
        marginBottom: '20px',
        border: `1px solid ${theme.colors.border}`
      }}>
        <div className="email-list-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
            📧 Email-uri ({emails.length})
          </h2>

          {/* Acțiuni bulk */}
          <div className="email-actions" style={{ display: 'flex', gap: '8px' }}>
            {selectedEmails.size > 0 && (
              <>
                <button
                  onClick={handleBulkDelete}
                  disabled={deleting}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: theme.colors.danger,
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: theme.borderRadius.small,
                    fontSize: '14px',
                    cursor: deleting ? 'not-allowed' : 'pointer',
                    opacity: deleting ? 0.6 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {deleting ? '⏳' : '🗑️'} Șterge {selectedEmails.size} selectate
                </button>
                <button onClick={() => setShowBulkEdit(true)} style={{
                  padding: '6px 12px', fontSize: '12px', border: '1px solid var(--border-color, #334155)',
                  borderRadius: '6px', backgroundColor: 'var(--card-bg, #1e293b)',
                  color: 'var(--text-primary, #e2e8f0)', cursor: 'pointer'
                }}>✏️ Editare in masa</button>
              </>
            )}
            
            {emails.length > 0 && (
              <button
                onClick={handleExportCsv}
                style={{
                  padding: '8px 16px',
                  backgroundColor: theme.colors.primary + '22',
                  color: theme.colors.primary,
                  border: `1px solid ${theme.colors.primary}44`,
                  borderRadius: theme.borderRadius.small,
                  fontSize: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                📥 Export CSV
              </button>
            )}

            {emails.length > 0 && (
              <button
                onClick={handleDeleteAll}
                disabled={deleting}
                style={{
                  padding: '8px 16px',
                  backgroundColor: theme.colors.danger,
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: theme.borderRadius.small,
                  fontSize: '14px',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  opacity: deleting ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {deleting ? '⏳' : '💣'} Șterge toate
              </button>
            )}
          </div>
        </div>
        
        {/* Controale de filtrare */}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {/* Search */}
          <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
            <span style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '16px'
            }}>
              🔍
            </span>
            <input
              type="text"
              placeholder="Caută email-uri..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px 8px 36px',
                backgroundColor: theme.colors.background,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: theme.borderRadius.small,
                fontSize: '14px',
                color: theme.colors.text,
                outline: 'none'
              }}
            />
          </div>
          
          {/* Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'scheduled' | 'sent' | 'failed' | 'upcoming')}
            style={{
              padding: '8px 16px',
              backgroundColor: theme.colors.background,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.small,
              fontSize: '14px',
              color: theme.colors.text,
              cursor: 'pointer',
              outline: 'none'
            }}
          >
            <option value="all">Toate</option>
            <option value="scheduled">Programate</option>
            <option value="sent">Trimise</option>
            <option value="failed">Eșuate</option>
            <option value="upcoming">Urmatoarele 7 zile</option>
          </select>
        </div>
      </div>

      {filteredEmails.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: theme.colors.textSecondary }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
          <h3 style={{ marginBottom: '8px' }}>
            {searchTerm || statusFilter !== 'all' 
              ? 'Nu s-au găsit email-uri care să corespundă filtrelor' 
              : 'Nu există email-uri'}
          </h3>
          <p>
            {searchTerm || statusFilter !== 'all' 
              ? 'Încearcă alte criterii de căutare' 
              : 'Începe prin a programa un email nou!'}
          </p>
        </div>
      ) : (
        <>
          {/* Select all */}
          <div style={{
            padding: '12px 20px',
            backgroundColor: theme.colors.surface,
            border: `1px solid ${theme.colors.border}`,
            borderRadius: `${theme.borderRadius.large} ${theme.borderRadius.large} 0 0`,
            borderBottom: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <input
              type="checkbox"
              checked={selectAll}
              onChange={toggleSelectAll}
              style={{
                width: '18px',
                height: '18px',
                cursor: 'pointer'
              }}
            />
            <label 
              onClick={toggleSelectAll}
              style={{ 
                cursor: 'pointer', 
                fontSize: '14px',
                color: theme.colors.textSecondary
              }}
            >
              Selectează toate ({filteredEmails.length})
            </label>
          </div>
          
          {/* Lista de email-uri */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
            {filteredEmails.map((email, index) => {
              const isExpanded = expandedEmails.has(email.id);
              const isSelected = selectedEmails.has(email.id);
              const isDeleting = deletingIds.has(email.id);
              const badges = getStatusBadge(email);
              const nextRecurrence = calculateNextRecurrence(email);
              
              return (
                <div
                  key={`email-item-${email.id}`}
                  style={{
                    backgroundColor: isSelected ? theme.colors.primary + '11' : theme.colors.surface,
                    border: `1px solid ${theme.colors.border}`,
                    borderTop: index === 0 ? 'none' : `1px solid ${theme.colors.border}`,
                    borderRadius: index === 0 
                      ? '0' 
                      : index === filteredEmails.length - 1 
                        ? `0 0 ${theme.borderRadius.large} ${theme.borderRadius.large}`
                        : '0',
                    overflow: 'hidden',
                    transition: theme.transitions.normal,
                    opacity: isDeleting ? 0.5 : 1
                  }}
                  className="email-item"
                >
                  {/* Header principal */}
                  <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                    {/* Checkbox */}
                    <div style={{
                      padding: '20px 0 20px 20px',
                      display: 'flex',
                      alignItems: 'flex-start'
                    }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelectEmail(email.id)}
                        onClick={(e) => e.stopPropagation()}
                        disabled={isDeleting}
                        style={{
                          width: '18px',
                          height: '18px',
                          marginTop: '2px',
                          cursor: isDeleting ? 'not-allowed' : 'pointer'
                        }}
                      />
                    </div>
                    
                    {/* Conținut email */}
                    <div
                      style={{
                        flex: 1,
                        padding: '20px',
                        paddingLeft: '16px',
                        cursor: 'pointer'
                      }}
                      onClick={() => !isDeleting && toggleExpand(email.id)}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          {/* Subiect și badges */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                              {email.subject}
                            </h3>
                            {badges.map(badge => (
                              <span
                                key={`badge-${email.id}-${badge.key}`}
                                style={{
                                  padding: '2px 8px',
                                  backgroundColor: badge.color + '22',
                                  color: badge.color,
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: '500',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                              >
                                {badge.icon} {badge.text}
                              </span>
                            ))}
                          </div>
                          
                          {/* Destinatari */}
                          <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: theme.colors.textSecondary }}>
                            Către: {email.to}
                          </p>
                          
                          {/* Informații temporale */}
                          <div style={{ fontSize: '13px', color: theme.colors.textSecondary }}>
                            {email.status === 'sent' ? (
                              <>
                                <span>✅ Trimis la: {new Date(email.sentAt!).toLocaleString('ro-RO')}</span>
                                {email.isRecurring && nextRecurrence && (
                                  <span style={{ marginLeft: '16px' }}>
                                    🔄 Următoarea trimitere: {nextRecurrence.toLocaleString('ro-RO')}
                                  </span>
                                )}
                              </>
                            ) : email.status === 'scheduled' ? (
                              <span>📅 Programat pentru: {new Date(email.scheduledFor).toLocaleString('ro-RO')}</span>
                            ) : (
                              <span>❌ Eșuat: {email.errorMessage || 'Eroare necunoscută'}</span>
                            )}
                          </div>
                          
                          {/* Preview conținut */}
                          {!isExpanded && (
                            <p style={{
                              margin: '8px 0 0 0',
                              fontSize: '13px',
                              color: theme.colors.textSecondary,
                              opacity: 0.8
                            }}>
                              {getContentPreview(email.content)}
                            </p>
                          )}
                        </div>
                        
                        {/* Acțiuni */}
                        <div className="email-row-actions" style={{ display: 'flex', gap: '4px', marginLeft: '16px', flexWrap: 'wrap' }}>
                          {/* Preview */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewEmail(email);
                            }}
                            disabled={isDeleting}
                            style={{
                              padding: '6px 10px',
                              backgroundColor: theme.colors.primary + '22',
                              color: theme.colors.primary,
                              border: 'none',
                              borderRadius: theme.borderRadius.small,
                              fontSize: '12px',
                              cursor: isDeleting ? 'not-allowed' : 'pointer',
                              opacity: isDeleting ? 0.5 : 1
                            }}
                            title="Preview"
                          >
                            👁️
                          </button>

                          {/* Edit - for scheduled emails */}
                          {email.status === 'scheduled' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onEmailEdit(email);
                              }}
                              disabled={isDeleting}
                              style={{
                                padding: '6px 10px',
                                backgroundColor: theme.colors.accent + '22',
                                color: theme.colors.accent,
                                border: 'none',
                                borderRadius: theme.borderRadius.small,
                                fontSize: '12px',
                                cursor: isDeleting ? 'not-allowed' : 'pointer',
                                opacity: isDeleting ? 0.5 : 1
                              }}
                              title="Editează"
                            >
                              ✏️
                            </button>
                          )}

                          {/* Reuse - for sent emails */}
                          {email.status === 'sent' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onEmailEdit(email);
                              }}
                              disabled={isDeleting}
                              style={{
                                padding: '6px 10px',
                                backgroundColor: theme.colors.accent + '22',
                                color: theme.colors.accent,
                                border: 'none',
                                borderRadius: theme.borderRadius.small,
                                fontSize: '12px',
                                cursor: isDeleting ? 'not-allowed' : 'pointer',
                                opacity: isDeleting ? 0.5 : 1
                              }}
                              title="Refolosește"
                            >
                              🔄
                            </button>
                          )}

                          {/* Duplicate */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDuplicate(email.id);
                            }}
                            disabled={isDeleting || duplicatingIds.has(email.id)}
                            style={{
                              padding: '6px 10px',
                              backgroundColor: '#8b5cf622',
                              color: '#8b5cf6',
                              border: 'none',
                              borderRadius: theme.borderRadius.small,
                              fontSize: '12px',
                              cursor: (isDeleting || duplicatingIds.has(email.id)) ? 'not-allowed' : 'pointer',
                              opacity: (isDeleting || duplicatingIds.has(email.id)) ? 0.5 : 1
                            }}
                            title="Duplică"
                          >
                            {duplicatingIds.has(email.id) ? '⏳' : '📋'}
                          </button>

                          {/* Retry - only for failed */}
                          {email.status === 'failed' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRetry(email.id);
                              }}
                              disabled={isDeleting || retryingIds.has(email.id)}
                              style={{
                                padding: '6px 10px',
                                backgroundColor: '#f59e0b22',
                                color: '#f59e0b',
                                border: 'none',
                                borderRadius: theme.borderRadius.small,
                                fontSize: '12px',
                                cursor: (isDeleting || retryingIds.has(email.id)) ? 'not-allowed' : 'pointer',
                                opacity: (isDeleting || retryingIds.has(email.id)) ? 0.5 : 1
                              }}
                              title="Reîncearcă"
                            >
                              {retryingIds.has(email.id) ? '⏳' : '🔁'}
                            </button>
                          )}

                          {/* Delete */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSingleDelete(email.id);
                            }}
                            disabled={isDeleting}
                            style={{
                              padding: '6px 10px',
                              backgroundColor: theme.colors.danger + '22',
                              color: theme.colors.danger,
                              border: 'none',
                              borderRadius: theme.borderRadius.small,
                              fontSize: '12px',
                              cursor: isDeleting ? 'not-allowed' : 'pointer',
                              opacity: isDeleting ? 0.5 : 1
                            }}
                            title="Șterge permanent"
                          >
                            {isDeleting ? '⏳' : '🗑️'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Detalii expandate */}
                  {isExpanded && (
                    <div style={{
                      padding: '0 20px 20px 54px',
                      borderTop: `1px solid ${theme.colors.border}`
                    }}>
                      {/* Preview complet */}
                      <div style={{
                        marginTop: '20px',
                        padding: '20px',
                        backgroundColor: theme.colors.background,
                        borderRadius: theme.borderRadius.medium,
                        border: `1px solid ${theme.colors.border}`
                      }}>
                        <h4 style={{ margin: '0 0 12px 0', fontSize: '14px' }}>
                          📧 Preview Email:
                        </h4>
                        {email.content.includes('<') && email.content.includes('>') ? (
                          <div 
                            style={{
                              backgroundColor: '#ffffff',
                              padding: '16px',
                              borderRadius: theme.borderRadius.small,
                              maxHeight: '400px',
                              overflow: 'auto'
                            }}
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(email.content) }}
                          />
                        ) : (
                          <pre style={{
                            margin: 0,
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'inherit',
                            fontSize: '14px',
                            lineHeight: 1.6
                          }}>
                            {email.content}
                          </pre>
                        )}
                      </div>
                      
                      {/* Informații adiționale */}
                      <div style={{
                        marginTop: '16px',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '16px',
                        fontSize: '13px'
                      }}>
                        <div>
                          <strong>Tip email:</strong> {email.emailType || 'transactional'}
                        </div>
                        <div>
                          <strong>Prioritate:</strong> {email.priority || 'normal'}
                        </div>
                        {email.isRecurring && (
                          <div>
                            <strong>Frecvență:</strong> {email.recurringType}
                          </div>
                        )}
                        {email.reminderDays && email.reminderDays > 0 && (
                          <div>
                            <strong>Reminder:</strong> {email.reminderDays} zile înainte
                            {email.reminderSentAt && (
                              <span style={{ color: theme.colors.success }}>
                                {' '}(trimis la {new Date(email.reminderSentAt).toLocaleDateString('ro-RO')})
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
      {/* Preview Modal */}
      {previewEmail && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={() => setPreviewEmail(null)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: theme.colors.surface,
              borderRadius: theme.borderRadius.large,
              padding: '24px',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '85vh',
              overflow: 'auto',
              border: `1px solid ${theme.colors.border}`
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}>📧 Preview: {previewEmail.subject}</h3>
              <button
                onClick={() => setPreviewEmail(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: theme.colors.text,
                  fontSize: '20px',
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>
            </div>
            <div style={{ marginBottom: '12px', fontSize: '14px', color: theme.colors.textSecondary }}>
              <strong>Către:</strong> {previewEmail.to} | <strong>Status:</strong> {previewEmail.status}
            </div>
            {previewEmail.content.includes('<') && previewEmail.content.includes('>') ? (
              <iframe
                srcDoc={previewEmail.content}
                style={{
                  width: '100%',
                  height: '400px',
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: theme.borderRadius.small,
                  backgroundColor: '#ffffff'
                }}
                sandbox="allow-same-origin"
                title="Email Preview"
              />
            ) : (
              <pre style={{
                padding: '16px',
                backgroundColor: theme.colors.background,
                borderRadius: theme.borderRadius.small,
                border: `1px solid ${theme.colors.border}`,
                whiteSpace: 'pre-wrap',
                fontFamily: 'inherit',
                fontSize: '14px',
                maxHeight: '400px',
                overflow: 'auto'
              }}>
                {previewEmail.content}
              </pre>
            )}
            <div style={{ marginTop: '16px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { onEmailEdit(previewEmail); setPreviewEmail(null); }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: theme.colors.accent,
                  color: '#fff',
                  border: 'none',
                  borderRadius: theme.borderRadius.small,
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                {previewEmail.status === 'scheduled' ? '✏️ Editează' : '🔄 Refolosește'}
              </button>
              <button
                onClick={() => setPreviewEmail(null)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: theme.colors.primary,
                  color: '#fff',
                  border: 'none',
                  borderRadius: theme.borderRadius.small,
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                Închide
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Edit Modal */}
      {showBulkEdit && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowBulkEdit(false); }}>
          <div style={{ backgroundColor: 'var(--card-bg, #1e293b)', borderRadius: '12px', padding: '24px', width: '400px', maxWidth: '90%' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '18px' }}>Editare in masa ({selectedEmails.size} email-uri)</h3>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', marginBottom: '4px', color: 'var(--text-secondary, #94a3b8)' }}>Data noua (optional)</label>
              <input type="datetime-local" value={bulkEditData.scheduled_time}
                onChange={e => setBulkEditData(prev => ({ ...prev, scheduled_time: e.target.value }))}
                style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--border-color, #334155)', backgroundColor: 'var(--input-bg, #0f172a)', color: 'var(--text-primary, #e2e8f0)', fontSize: '13px', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', marginBottom: '4px', color: 'var(--text-secondary, #94a3b8)' }}>Prioritate (optional)</label>
              <select value={bulkEditData.priority}
                onChange={e => setBulkEditData(prev => ({ ...prev, priority: e.target.value }))}
                style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--border-color, #334155)', backgroundColor: 'var(--input-bg, #0f172a)', color: 'var(--text-primary, #e2e8f0)', fontSize: '13px' }}>
                <option value="">-- Nu schimba --</option>
                <option value="low">Scazuta</option>
                <option value="normal">Normala</option>
                <option value="high">Ridicata</option>
                <option value="urgent">Urgenta</option>
              </select>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', marginBottom: '4px', color: 'var(--text-secondary, #94a3b8)' }}>Status (optional)</label>
              <select value={bulkEditData.status}
                onChange={e => setBulkEditData(prev => ({ ...prev, status: e.target.value }))}
                style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--border-color, #334155)', backgroundColor: 'var(--input-bg, #0f172a)', color: 'var(--text-primary, #e2e8f0)', fontSize: '13px' }}>
                <option value="">-- Nu schimba --</option>
                <option value="scheduled">Reprogrameaza</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowBulkEdit(false)} style={{ padding: '8px 16px', border: '1px solid var(--border-color, #334155)', borderRadius: '6px', backgroundColor: 'transparent', color: 'var(--text-secondary, #94a3b8)', cursor: 'pointer', fontSize: '13px' }}>Anuleaza</button>
              <button onClick={async () => {
                const updates: Record<string, unknown> = {};
                if (bulkEditData.scheduled_time) updates.scheduled_time = bulkEditData.scheduled_time;
                if (bulkEditData.priority) updates.priority = bulkEditData.priority;
                if (bulkEditData.status) updates.status = bulkEditData.status;
                if (Object.keys(updates).length === 0) return;
                try {
                  await emailApi.bulkEditEmails(Array.from(selectedEmails), updates);
                  setShowBulkEdit(false);
                  setBulkEditData({ scheduled_time: '', priority: '', status: '' });
                  setSelectedEmails(new Set());
                  if (onRefresh) onRefresh();
                } catch {}
              }} style={{ padding: '8px 20px', border: 'none', borderRadius: '6px', backgroundColor: '#3498db', color: 'white', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}>Aplica</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        variant={confirmState.variant}
        onConfirm={confirmState.onConfirm}
        onCancel={cancelConfirm}
      />
    </div>
  );
};

export default EmailList;